/**
 * SyntaxAnalysisContext - Contexto para análisis de Sintaxis Espacial
 *
 * Este contexto gestiona:
 * - Estado del cálculo de sintaxis (idle, calculating, ready, error)
 * - Métricas calculadas por edge
 * - Rangos min/max para normalización de colores
 * - Métrica seleccionada para visualización
 * - Modo de visualización activo/inactivo
 *
 * @example
 * ```typescript
 * const {
 *   status,
 *   calculateSyntax,
 *   syntaxModeActive,
 *   setSyntaxModeActive,
 *   selectedMetric,
 *   setSelectedMetric,
 * } = useSyntaxAnalysis();
 *
 * // Calcular sintaxis
 * await calculateSyntax(800);
 *
 * // Activar visualización
 * setSyntaxModeActive(true);
 *
 * // Cambiar métrica
 * setSelectedMetric('choice_global');
 * ```
 */

/* eslint-disable max-lines, max-lines-per-function, complexity, @typescript-eslint/no-misused-promises */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import { ProgressToast } from '../../components';
import { useProject } from '../../projects';
import {
  syntaxApi,
  SyntaxMetric,
  SyntaxMetricSlimEdge,
  SyntaxRange,
  MetricRange,
} from '../services/syntax-api.service';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Tipos de métricas de sintaxis disponibles (multi-radio)
 * @see PLAN_SINTAXIS_ESPACIAL_PIPELINE.md
 */
export type SyntaxMetricType =
  // Integration metrics (4 radios: global, global_raw, r1200, r800)
  | 'integration_global'
  | 'integration_global_raw'
  | 'integration_r1200'
  | 'integration_r800'
  // Choice metrics (3 radios: global, r1200, r800)
  | 'choice_global'
  | 'choice_r1200'
  | 'choice_r800';

/**
 * Estados posibles del análisis
 */
export type SyntaxStatus = 'idle' | 'calculating' | 'loading' | 'ready' | 'error';

/**
 * Valor del contexto de análisis de sintaxis
 */
export interface SyntaxAnalysisContextValue {
  // ── Estado ──
  /** Estado actual del análisis */
  status: SyntaxStatus;
  /** Métricas completas por edge (7 columnas) — para EdgeDetailsPanel */
  metrics: SyntaxMetric[];
  /** Métricas slim de la métrica seleccionada — para visualización del mapa */
  slimMetrics: SyntaxMetricSlimEdge[];
  /** Rangos min/max para normalización */
  range: SyntaxRange | null;
  /** Rango real de la métrica actualmente seleccionada */
  currentMetricDataRange: MetricRange | null;
  /** Rango visual activo de la métrica actualmente seleccionada */
  currentMetricVisualRange: MetricRange | null;
  /** Fecha del último cálculo */
  calculatedAt: Date | null;
  /** Radio local usado en el cálculo */
  localRadiusM: number;
  /** Mensaje de error si hay */
  error: string | null;

  // ── Pipeline Status (Traceability) ──
  /** Versión de sintaxis actual */
  syntaxVersion: number;
  /** Versión de red urbana sobre la que se calculó */
  syntaxBasedOnNetworkVersion: number | null;
  /** Versión actual de la red urbana */
  urbanNetworkVersion: number;
  /** Si la sintaxis está desactualizada respecto a la red */
  isStale: boolean;

  // ── Visualización ──
  /** Métrica seleccionada para visualizar */
  selectedMetric: SyntaxMetricType;
  /** Si el modo de visualización por sintaxis está activo */
  syntaxModeActive: boolean;
  /** Curva de contraste aplicada a la normalización visual */
  contrastExponent: number;

  // ── Acciones ──
  /** Encolar recálculo de sintaxis espacial (se ejecuta en background) */
  calculateSyntax: () => Promise<void>;
  /** Cargar métricas slim para la métrica seleccionada (con cache en memoria) */
  loadSlimMetrics: (metric?: SyntaxMetricType) => Promise<void>;
  /** Cargar métricas completas (7 columnas) para EdgeDetailsPanel */
  loadMetrics: () => Promise<void>;
  /** Cambiar métrica seleccionada */
  setSelectedMetric: (metric: SyntaxMetricType) => void;
  /** Activar/desactivar modo de visualización */
  setSyntaxModeActive: (active: boolean) => void;
  /** Cambiar radio local */
  setLocalRadiusM: (radius: number) => void;
  /** Ajustar rango visual de la métrica seleccionada */
  setCurrentMetricVisualRange: (range: MetricRange | null) => void;
  /** Resetear rango visual al rango real de la métrica seleccionada */
  resetCurrentMetricVisualRange: () => void;
  /** Ajustar curva de contraste visual */
  setContrastExponent: (value: number) => void;
  /**
   * Precarga métricas en background de forma segura.
   * No hace nada si ya están cargadas o en carga para el proyecto actual.
   * Usar para precarga diferida después de que la red esté lista.
   */
  preloadMetrics: () => Promise<void>;
}

function getMetricRangeByType(
  range: SyntaxRange | null,
  metric: SyntaxMetricType
): MetricRange | null {
  if (!range) {
    return null;
  }

  switch (metric) {
    case 'integration_global':
      return range.integrationGlobal;
    case 'integration_global_raw':
      return range.integrationGlobalRaw;
    case 'integration_r1200':
      return range.integrationR1200;
    case 'integration_r800':
      return range.integrationR800;
    case 'choice_global':
      return range.choiceGlobal;
    case 'choice_r1200':
      return range.choiceR1200;
    case 'choice_r800':
      return range.choiceR800;
    default:
      return null;
  }
}

// ============================================================================
// CONTEXTO
// ============================================================================

const SyntaxAnalysisContext = createContext<SyntaxAnalysisContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

export interface SyntaxAnalysisProviderProps {
  children: ReactNode;
}

export function SyntaxAnalysisProvider({ children }: SyntaxAnalysisProviderProps): JSX.Element {
  const { projectId } = useProject();

  // ── Ref para evitar race conditions cuando el proyecto cambia ──
  const currentProjectIdRef = useRef<string | null>(null);
  // ── Ref para carga diferida: evita recargar métricas si ya se cargaron para este proyecto ──
  const metricsLoadedForProjectRef = useRef<string | null>(null);
  // ── Ref para el intervalo de polling — permite limpiarlo en unmount y al desactivar modo ──
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // ── Ref espejo de syntaxModeActive para acceso dentro de callbacks async ──
  const syntaxModeActiveRef = useRef(false);
  // ── Ref espejo de selectedMetric — permite leerlo en callbacks sin añadirlo a deps ──
  const selectedMetricRef = useRef<SyntaxMetricType>('integration_global');
  // ── Cache en memoria de slim metrics por métrica: evita re-fetch al cambiar métrica ──
  const slimMetricsCacheRef = useRef<Partial<Record<SyntaxMetricType, SyntaxMetricSlimEdge[]>>>({});

  // ── Estado ──
  const [status, setStatus] = useState<SyntaxStatus>('idle');
  const [metrics, setMetrics] = useState<SyntaxMetric[]>([]);
  const [slimMetrics, setSlimMetrics] = useState<SyntaxMetricSlimEdge[]>([]);
  const [range, setRange] = useState<SyntaxRange | null>(null);
  const [calculatedAt, setCalculatedAt] = useState<Date | null>(null);
  const [localRadiusM, setLocalRadiusM] = useState(800);
  const [error, setError] = useState<string | null>(null);

  // ── Pipeline Status (Traceability) ──
  const [syntaxVersion, setSyntaxVersion] = useState(0);
  const [syntaxBasedOnNetworkVersion, setSyntaxBasedOnNetworkVersion] = useState<number | null>(
    null
  );
  const [urbanNetworkVersion, setUrbanNetworkVersion] = useState(0);
  const [isStale, setIsStale] = useState(false);

  // ── Visualización ──
  const [selectedMetric, setSelectedMetricState] = useState<SyntaxMetricType>('integration_global');
  const [syntaxModeActive, setSyntaxModeActive] = useState(false);
  const [visualRangeOverrides, setVisualRangeOverrides] = useState<
    Partial<Record<SyntaxMetricType, MetricRange>>
  >({});
  const [contrastExponent, setContrastExponent] = useState(1);

  // ── Progress Toast ──
  const [edgeCount, setEdgeCount] = useState(0);

  const currentMetricDataRange = useMemo(
    () => getMetricRangeByType(range, selectedMetric),
    [range, selectedMetric]
  );

  const currentMetricVisualRange = useMemo(() => {
    const dataRange = currentMetricDataRange;
    if (!dataRange) {
      return null;
    }

    const override = visualRangeOverrides[selectedMetric];
    if (!override) {
      return dataRange;
    }

    const min = Math.max(dataRange.min, Math.min(override.min, override.max));
    const max = Math.min(dataRange.max, Math.max(override.min, override.max));

    if (min === max) {
      return dataRange;
    }

    return { min, max };
  }, [currentMetricDataRange, selectedMetric, visualRangeOverrides]);

  const setCurrentMetricVisualRange = useCallback(
    (nextRange: MetricRange | null) => {
      setVisualRangeOverrides(previous => {
        if (!nextRange) {
          const { [selectedMetric]: _removed, ...rest } = previous;
          return rest;
        }

        return {
          ...previous,
          [selectedMetric]: nextRange,
        };
      });
    },
    [selectedMetric]
  );

  const resetCurrentMetricVisualRange = useCallback(() => {
    setVisualRangeOverrides(previous => {
      const { [selectedMetric]: _removed, ...rest } = previous;
      return rest;
    });
  }, [selectedMetric]);

  /**
   * Carga slim metrics para la métrica indicada (o la seleccionada actualmente).
   *
   * Flujo:
   * 1. Comprueba cache en memoria: si hay hit, actualiza slimMetrics sin fetch.
   * 2. Si hay miss: fetch liviano (~1/7 del payload full) + getRange en paralelo.
   * 3. Guarda en cache para futuras llamadas sin cost de red.
   *
   * Este es el driver principal de `status` — cuando completa, el mapa puede pintar.
   *
   * @param metric - Métrica a cargar (opcional: usa selectedMetricRef si se omite)
   */
  const loadSlimMetrics = useCallback(
    async (metric?: SyntaxMetricType) => {
      if (!projectId) return;

      const targetMetric = metric ?? selectedMetricRef.current;
      const requestProjectId = projectId;

      // ── Cache hit: no hay fetch ──
      const cached = slimMetricsCacheRef.current[targetMetric];
      if (cached) {
        setSlimMetrics(cached);
        setStatus('ready');
        console.log(
          '[SyntaxAnalysisContext] slimMetrics cache hit:',
          targetMetric,
          `(${cached.length} edges)`
        );
        return;
      }

      // ── Cache miss: fetch slim + range ──
      setStatus('loading');
      setError(null);

      try {
        const [slimData, rangeData] = await Promise.all([
          syntaxApi.getMetricSlim(projectId, targetMetric),
          syntaxApi.getRange(projectId),
        ]);

        if (currentProjectIdRef.current !== requestProjectId) {
          console.log(
            '[SyntaxAnalysisContext] Ignoring stale slim response for project:',
            requestProjectId
          );
          return;
        }

        slimMetricsCacheRef.current[targetMetric] = slimData.edges;
        setSlimMetrics(slimData.edges);
        setRange(rangeData);
        setEdgeCount(slimData.edges.length);
        setStatus(slimData.hasMetrics ? 'ready' : 'idle');

        console.log(
          '[SyntaxAnalysisContext] Loaded slim metrics:',
          targetMetric,
          '-',
          slimData.edges.length,
          'edges',
          slimData.hasMetrics ? '(has metrics)' : '(no metrics)'
        );
      } catch (e: unknown) {
        if (currentProjectIdRef.current !== requestProjectId) {
          console.log(
            '[SyntaxAnalysisContext] Ignoring slim error for stale project:',
            requestProjectId
          );
          return;
        }
        const errorMessage = e instanceof Error ? e.message : 'Unknown error';
        setError(errorMessage);
        setStatus('error');
        console.error('[SyntaxAnalysisContext] Error loading slim metrics:', errorMessage);
      }
    },
    [projectId]
  );

  /**
   * Carga métricas completas (7 columnas) desde el backend — para EdgeDetailsPanel.
   *
   * No cambia `status`: corre en background tras loadSlimMetrics para no bloquear
   * la visualización del mapa. Si falla, el panel muestra estado vacío silenciosamente.
   *
   * @remarks
   * Usa currentProjectIdRef para evitar race conditions cuando el proyecto
   * cambia mientras una request está en vuelo.
   */
  const loadMetrics = useCallback(async () => {
    if (!projectId) return;

    const requestProjectId = projectId;

    try {
      const metricsData = await syntaxApi.getMetrics(projectId);

      if (currentProjectIdRef.current !== requestProjectId) {
        console.log(
          '[SyntaxAnalysisContext] Ignoring stale full metrics for project:',
          requestProjectId
        );
        return;
      }

      setMetrics(metricsData.edges);

      if (metricsData.calculatedAt) {
        setCalculatedAt(new Date(metricsData.calculatedAt));
      }

      // Update pipeline status (traceability) — slim load ya actualizó status,
      // aquí completamos la info de versionado con la respuesta completa.
      setSyntaxVersion(metricsData.syntaxVersion ?? 0);
      setSyntaxBasedOnNetworkVersion(metricsData.syntaxBasedOnNetworkVersion ?? null);
      setUrbanNetworkVersion(metricsData.urbanNetworkVersion ?? 0);
      setIsStale(metricsData.isStale ?? false);

      console.log(
        '[SyntaxAnalysisContext] Full metrics loaded for project:',
        requestProjectId,
        '-',
        metricsData.edges.length,
        'edges (EdgeDetailsPanel ready)'
      );
    } catch (e: unknown) {
      if (currentProjectIdRef.current !== requestProjectId) return;
      // Fallo silencioso: el mapa ya pintó (slim ok), solo el panel queda vacío
      console.error(
        '[SyntaxAnalysisContext] Background full metrics load failed:',
        e instanceof Error ? e.message : e
      );
    }
  }, [projectId]);

  /**
   * Cambia la métrica seleccionada y dispara carga slim si el modo está activo.
   * Usa cache en memoria para evitar fetch si la métrica ya fue cargada antes.
   *
   * Definido DESPUÉS de loadSlimMetrics para poder incluirlo en deps sin closure stale.
   */
  const setSelectedMetric = useCallback(
    (metric: SyntaxMetricType) => {
      selectedMetricRef.current = metric;
      setSelectedMetricState(metric);
      if (syntaxModeActiveRef.current && projectId) {
        void loadSlimMetrics(metric);
      }
    },
    [projectId, loadSlimMetrics]
  );

  /**
   * Precarga métricas en background de forma segura (dedup-safe).
   * Carga slim primero (rápido, para mapa) y full en background (para panel).
   * No dispara request si ya están cargadas o en carga para este proyecto.
   */
  const preloadMetrics = useCallback(async () => {
    if (!projectId) return;

    if (metricsLoadedForProjectRef.current === projectId) {
      console.log(
        '[SyntaxAnalysisContext] preloadMetrics: already loading/loaded for project:',
        projectId
      );
      return;
    }

    if (status === 'loading' || status === 'ready') return;

    metricsLoadedForProjectRef.current = projectId;
    console.log('[SyntaxAnalysisContext] Background preload started for project:', projectId);
    await loadSlimMetrics();
    void loadMetrics();
  }, [projectId, status, loadSlimMetrics, loadMetrics]);

  /**
   * Encola recálculo de sintaxis espacial.
   * El cálculo corre en background (Bull Queue). Durante la espera, hace polling
   * liviano a /syntax/status (~300 bytes) en lugar de /syntax/metrics (1-2 MB).
   * Solo descarga métricas una vez cuando el backend reporta status 'valid'.
   */
  const calculateSyntax = useCallback(async () => {
    if (!projectId) return;

    const requestProjectId = projectId;
    setStatus('calculating');
    setError(null);

    // Cancelar cualquier polling anterior antes de arrancar uno nuevo
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    console.log(
      '[SyntaxAnalysisContext] Enqueuing syntax recalculation for project:',
      requestProjectId
    );

    try {
      // Encolar job de recálculo (202 ACCEPTED, retorna inmediatamente)
      const result = await syntaxApi.recalculate(projectId);

      // Race condition check post-enqueue
      if (currentProjectIdRef.current !== requestProjectId) {
        console.log(
          '[SyntaxAnalysisContext] Ignoring recalculate result for stale project:',
          requestProjectId
        );
        return;
      }

      console.log('[SyntaxAnalysisContext] Recalculation enqueued:', result.jobId);

      // Polling liviano: GET /status (~300 bytes) cada 3s
      // Solo llama /metrics una vez al detectar 'valid'
      const maxAttempts = 60; // 3 minutos máximo
      let attempts = 0;

      console.log('[SyntaxPolling] status polling started');

      pollIntervalRef.current = setInterval(async () => {
        attempts++;

        // Detener si el proyecto cambió
        if (currentProjectIdRef.current !== requestProjectId) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          console.log('[SyntaxPolling] stopped — project changed');
          return;
        }

        // Detener si el modo sintaxis fue desactivado
        if (!syntaxModeActiveRef.current) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          console.log('[SyntaxPolling] stopped — syntax mode deactivated');
          setStatus('idle');
          return;
        }

        // Timeout después de maxAttempts
        if (attempts >= maxAttempts) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          console.warn('[SyntaxPolling] stopped — timeout after', maxAttempts * 3, 'seconds');
          setError(
            'El cálculo está tardando más de lo esperado. Recarga la página para ver el estado actualizado.'
          );
          setStatus('error');
          return;
        }

        try {
          // Llamada liviana a /status (no /metrics)
          const statusData = await syntaxApi.getStatus(requestProjectId);

          if (statusData.syntaxStatus === 'valid') {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            console.log('[SyntaxPolling] status ready — loading slim metrics');
            // Invalidar cache: el recálculo produjo nuevos valores
            slimMetricsCacheRef.current = {};
            await loadSlimMetrics(); // rápido → status 'ready' → mapa pinta
            void loadMetrics(); // background → panel actualiza silenciosamente
          } else if (statusData.syntaxStatus === 'error') {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            console.log('[SyntaxPolling] status failed');
            setError('Error durante el cálculo de sintaxis');
            setStatus('error');
          } else {
            console.log(
              `[SyntaxPolling] status pending — attempt ${attempts}/${maxAttempts}: ${statusData.syntaxStatus}`
            );
          }
        } catch (pollError) {
          console.error('[SyntaxPolling] error during status check:', pollError);
        }
      }, 3000);
    } catch (e: unknown) {
      if (currentProjectIdRef.current !== requestProjectId) {
        console.log(
          '[SyntaxAnalysisContext] Ignoring recalculate error for stale project:',
          requestProjectId
        );
        return;
      }
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      setError(errorMessage);
      setStatus('error');
      console.error('[SyntaxAnalysisContext] Recalculation error:', errorMessage);
    }
  }, [projectId, loadSlimMetrics, loadMetrics]);

  // ── Mantener syntaxModeActiveRef sincronizado con el estado (para callbacks async) ──
  useEffect(() => {
    syntaxModeActiveRef.current = syntaxModeActive;
  }, [syntaxModeActive]);

  // ── Detener polling si el usuario desactiva el modo sintaxis mientras calcula ──
  useEffect(() => {
    if (!syntaxModeActive && pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      console.log('[SyntaxPolling] stopped — syntax mode deactivated');
    }
  }, [syntaxModeActive]);

  // ── Limpiar polling en unmount del provider ──
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        console.log('[SyntaxPolling] stopped — provider unmounted');
      }
    };
  }, []);

  // ── Resetear estado y tracking al cambiar de proyecto ──
  // Las métricas NO se cargan aquí: se cargan de forma diferida cuando syntaxModeActive=true
  useEffect(() => {
    currentProjectIdRef.current = projectId;
    metricsLoadedForProjectRef.current = null; // invalidar caché del proyecto anterior

    // Cancelar polling del proyecto anterior de inmediato (no esperar al siguiente tick del interval)
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      console.log('[SyntaxPolling] stopped — project changed');
    }

    // Invalidar caches del proyecto anterior
    slimMetricsCacheRef.current = {};

    if (projectId) {
      setVisualRangeOverrides({});
      setContrastExponent(1);
      setMetrics([]);
      setSlimMetrics([]);
      setRange(null);
      setCalculatedAt(null);
      setStatus('idle');
      setSyntaxVersion(0);
      setSyntaxBasedOnNetworkVersion(null);
      setUrbanNetworkVersion(0);
      setIsStale(false);
      console.log(
        '[SyntaxAnalysisContext] Project changed to:',
        projectId,
        '- metrics deferred until syntax mode active'
      );
    } else {
      // Reset completo si no hay proyecto
      setMetrics([]);
      setSlimMetrics([]);
      setRange(null);
      setCalculatedAt(null);
      setStatus('idle');
      setSyntaxModeActive(false);
      setVisualRangeOverrides({});
      setContrastExponent(1);
      setSyntaxVersion(0);
      setSyntaxBasedOnNetworkVersion(null);
      setUrbanNetworkVersion(0);
      setIsStale(false);
    }
  }, [projectId]); // no depende de loadMetrics — carga diferida

  // ── Cargar métricas cuando el usuario activa la visualización de sintaxis ──
  // Estrategia de carga progresiva (S2):
  //   1. Slim metrics (rápido, ~1/7 del payload) → status 'ready' → mapa pinta
  //   2. Full metrics en background → EdgeDetailsPanel actualiza silenciosamente
  useEffect(() => {
    if (syntaxModeActive && projectId && metricsLoadedForProjectRef.current !== projectId) {
      metricsLoadedForProjectRef.current = projectId;
      console.log(
        '[SyntaxAnalysisContext] Syntax mode activated - progressive load for project:',
        projectId
      );
      void loadSlimMetrics(); // rápido → mapa
      void loadMetrics(); // background → panel
    }
  }, [syntaxModeActive, projectId, loadSlimMetrics, loadMetrics]);

  // ── Valor del contexto ──
  const value: SyntaxAnalysisContextValue = {
    // Estado
    status,
    metrics,
    slimMetrics,
    range,
    currentMetricDataRange,
    currentMetricVisualRange,
    calculatedAt,
    localRadiusM,
    error,
    // Pipeline Status (Traceability)
    syntaxVersion,
    syntaxBasedOnNetworkVersion,
    urbanNetworkVersion,
    isStale,
    // Visualización
    selectedMetric,
    syntaxModeActive,
    contrastExponent,
    // Acciones
    calculateSyntax,
    loadSlimMetrics,
    loadMetrics,
    preloadMetrics,
    setSelectedMetric,
    setSyntaxModeActive,
    setLocalRadiusM,
    setCurrentMetricVisualRange,
    resetCurrentMetricVisualRange,
    setContrastExponent,
  };

  // Tiempo estimado: ~120s por cada 1000 edges para redes grandes (basado en benchmark real)
  // Para 18k edges: ~36 minutos = 2160 segundos
  const estimatedSeconds = Math.max(30, Math.ceil((edgeCount / 1000) * 120));

  return (
    <SyntaxAnalysisContext.Provider value={value}>
      {children}
      <ProgressToast
        message="Calculando Sintaxis Espacial..."
        statusMessage={edgeCount > 0 ? `${edgeCount.toLocaleString()} edges` : undefined}
        estimatedSeconds={estimatedSeconds}
        visible={status === 'calculating'}
      />
    </SyntaxAnalysisContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook para acceder al contexto de análisis de sintaxis
 *
 * @throws Error si se usa fuera de SyntaxAnalysisProvider
 */
export function useSyntaxAnalysis(): SyntaxAnalysisContextValue {
  const context = useContext(SyntaxAnalysisContext);
  if (!context) {
    throw new Error('useSyntaxAnalysis must be used within SyntaxAnalysisProvider');
  }
  return context;
}

/**
 * Hook seguro que retorna null si no hay provider
 */
export function useSyntaxAnalysisSafe(): SyntaxAnalysisContextValue | null {
  return useContext(SyntaxAnalysisContext);
}
